import clsx from 'clsx';
import Link from 'next/link';
// import Link from '@/components/Link';
import { FC, MouseEvent, ReactNode, useEffect, useRef } from 'react';

import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	href?: string;
	target?: '_blank' | '_self';
	rel?: string;
	className?: string;
	classNameText?: string;
	iconComponent?: ReactNode | ReactNode[];
	children: string;
	onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
	'aria-label'?: string;
}

const AnimatedLink: FC<Props> = ({
	href = '/',
	target = '_self',
	rel = 'noreferrer',
	className,
	classNameText = '',
	iconComponent: IconComponent = null,
	children,
	onClick,
	...props
}) => {
	const topRef = useRef<HTMLSpanElement>(null);
	const botRef = useRef<HTMLSpanElement>(null);

	const innerRef = useRef<HTMLButtonElement & HTMLAnchorElement>(null);
	const tlRef = useRef<gsap.core.Timeline | null>(null);

	const ariaLabel = props['aria-label'];
	const baseUri = process.env.NEXT_PUBLIC_WEBSITE_URL || '';
	const hrefValue = href?.includes('@') && !href?.includes('http') ? `mailto:${href}` : href;
	const targetValue =
		(href?.startsWith('http') && !href.includes(baseUri)) || target === '_blank'
			? '_blank'
			: undefined;

	useEffect(() => {
		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;
					if (isMobile) return;

					const top = topRef.current;
					const bot = botRef.current;

					if (!top || !bot) return;

					const tl = gsap.timeline({ paused: true });

					tl.to(
						top,
						{
							yPercent: -100,
							opacity: 0,
							duration: 0.15,
							ease: 'power2.out'
						},
						0
					).fromTo(
						bot,
						{ yPercent: 100, opacity: 0 },
						{ yPercent: 0, opacity: 1, duration: 0.15, ease: 'power2.out' },
						0.05
					);

					tlRef.current = tl;
				}
			);
		}, topRef);

		return () => {
			ctx.revert();
		};
	}, []);

	const onEnter = () => tlRef.current?.play();
	const onLeave = () => tlRef.current?.reverse();

	return (
		<Link
			ref={innerRef as any}
			href={hrefValue}
			target={targetValue}
			rel={rel}
			className={className}
			aria-label={ariaLabel}
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
			onClick={onClick}
		>
			<div className={clsx(css.glitchText, classNameText)} data-text={children}>
				<span ref={topRef} className={css.top}>
					{children}
				</span>
				<span ref={botRef} className={css.bot}>
					{children}
				</span>
			</div>
			{IconComponent ? <span className="pointer-events-none">{IconComponent}</span> : null}
		</Link>
	);
};

export default AnimatedLink;
